import { useEffect, useState, useRef } from 'react'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'
import { fetchPrices } from '../utils/api'
import './PriceChart.css'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

// Custom Chart.js plugin: raudona vertikali linija ties dabartine valanda
const currentTimeLinePlugin = {
  id: 'currentTimeLine',
  afterDraw(chart, _args, options) {
    if (!options.show) return
    const { ctx, scales: { x, y } } = chart
    const label = options.label
    if (!label) return

    const xIndex = chart.data.labels.indexOf(label)
    if (xIndex === -1) return

    const xPos = x.getPixelForValue(xIndex)
    const yTop = y.top
    const yBottom = y.bottom

    ctx.save()
    ctx.beginPath()
    ctx.moveTo(xPos, yTop)
    ctx.lineTo(xPos, yBottom)
    ctx.lineWidth = 2
    ctx.strokeStyle = 'rgba(239, 68, 68, 0.9)'
    ctx.setLineDash([6, 3])
    ctx.stroke()

    // Tekstas virš linijos
    ctx.setLineDash([])
    ctx.fillStyle = 'rgba(239, 68, 68, 0.9)'
    ctx.font = 'bold 11px sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText('Dabar', xPos, yTop - 4)
    ctx.restore()
  }
}

function PriceChart({ includeVAT = true, onPricesLoaded, insights = null }) {
  const [prices, setPrices] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [currentTime, setCurrentTime] = useState(new Date())

  const vatMultiplier = includeVAT ? 1.21 : 1.0

  // Apribojimai: galima žiūrėti tik 1 dieną į priekį
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const maxDate = new Date(today)
  maxDate.setDate(maxDate.getDate() + 1)
  maxDate.setHours(23, 59, 59, 999) // Atsarginė riba iki paros pabaigos

  useEffect(() => {
    fetchRealPrices(selectedDate)
  }, [selectedDate])

  // Atnaujinti dabartinį laiką kas 15 minučių
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date())
    }, 15 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  const fetchRealPrices = async (date) => {
    setLoading(true)
    setError(null)
    try {
      const dateStr = formatDate(date)
      const result = await fetchPrices(dateStr, dateStr)
      
      if (result.prices && Object.keys(result.prices).length > 0) {
        const formattedPrices = Object.entries(result.prices).map(([dt, price]) => ({
          datetime: dt,
          price: price
        })).sort((a, b) => new Date(a.datetime) - new Date(b.datetime))
        
        setPrices(formattedPrices)
        // Pranešame tėvui apie užkrautas kainas
        if (onPricesLoaded) {
          onPricesLoaded(formattedPrices, date)
        }
      } else {
        setPrices([])
        if (onPricesLoaded) onPricesLoaded([], date)
      }
    } catch (err) {
      console.error('Klaida užkraunant kainas:', err)
      setError('Nepavyko užkrauti realių kainų.')
    } finally {
      setLoading(false)
    }
  }

  // Pagalbinė funkcija rasti kategoriją konkrečiam taškui pagal valandą
  const getCategoryColor = (datetime) => {
    if (!insights || !insights.insights) return 'rgb(37, 99, 235)';
    const hour = new Date(datetime).getHours();
    const insight = insights.insights.find(i => i.hour === hour);
    return insight ? insight.color : 'rgb(37, 99, 235)';
  };

  const handlePreviousDay = () => {
    const newDate = new Date(selectedDate)
    newDate.setDate(newDate.getDate() - 1)
    setSelectedDate(newDate)
  }

  const handleNextDay = () => {
    const newDate = new Date(selectedDate)
    newDate.setDate(newDate.getDate() + 1)
    
    if (newDate > maxDate) return
    
    setSelectedDate(newDate)
  }

  const handleDateChange = (e) => {
    const newDate = new Date(e.target.value)
    if (newDate > maxDate) return
    setSelectedDate(newDate)
  }

  const formatDate = (date) => {
    // Naudojame vietinį laiką, kad išvengtume UTC nobelybių
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  const formatDisplayDate = (date) => {
    return date.toLocaleDateString('lt-LT', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      weekday: 'long'
    })
  }

  if (loading) {
    return <div className="chart-loading">Kraunama...</div>
  }

  if (error) {
    return <div className="chart-error">Klaida: {error}</div>
  }

  const chartData = {
    labels: prices.map(p => {
      const date = new Date(p.datetime)
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
    }),
    datasets: [
      {
        label: `Kaina (${includeVAT ? 'su PVM' : 'be PVM'}, EUR/kWh)`,
        data: prices.map(p => p.price * vatMultiplier),
        borderColor: 'rgb(37, 99, 235)',
        backgroundColor: 'rgba(37, 99, 235, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
        // Dinamiškas taškų spalvinimas
        pointBackgroundColor: prices.map(p => getCategoryColor(p.datetime)),
        pointBorderColor: prices.map(p => getCategoryColor(p.datetime)),
        // Linijos segmentų spalvinimas
        segment: {
          borderColor: ctx => getCategoryColor(prices[ctx.p0DataIndex]?.datetime),
        }
      }
    ]
  }

  // Nustatyti, ar rodoma šiandienos data
  const todayDate = new Date()
  todayDate.setHours(0, 0, 0, 0)
  const selectedDateOnly = new Date(selectedDate)
  selectedDateOnly.setHours(0, 0, 0, 0)
  const isToday = selectedDateOnly.getTime() === todayDate.getTime()

  // Dabartinės valandos label (pvz. "13:00")
  const currentHourLabel = `${currentTime.getHours().toString().padStart(2, '0')}:00`

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        labels: {
          color: '#f1f5f9'
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.parsed.y.toFixed(4)} EUR/kWh`
          }
        }
      },
      currentTimeLine: {
        show: isToday,
        label: currentHourLabel
      }
    },
    scales: {
      y: {
        beginAtZero: false,
        ticks: {
          color: '#94a3b8',
          callback: function(value) {
            return value.toFixed(2) + ' €'
          }
        },
        grid: {
          color: '#334155'
        }
      },
      x: {
        ticks: {
          color: '#94a3b8'
        },
        grid: {
          color: '#334155'
        }
      }
    }
  }

  return (
    <div className="price-chart">
      <div className="date-navigation">
        <button 
          className="nav-button"
          onClick={handlePreviousDay}
          title="Praeita diena"
        >
          <svg className="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Praeita
        </button>

        <div className="date-picker">
          <input
            type="date"
            value={formatDate(selectedDate)}
            onChange={handleDateChange}
            max={formatDate(maxDate)}
            className="date-input"
          />
          <div className="date-display">
            {formatDisplayDate(selectedDate)}
          </div>
        </div>

        <button 
          className="nav-button"
          onClick={handleNextDay}
          disabled={selectedDate >= maxDate}
          title="Kita diena"
        >
          Kita
          <svg className="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <div className="chart-container">
        {prices.length > 0 ? (
          <Line data={chartData} options={options} plugins={[currentTimeLinePlugin]} />
        ) : (
          <div className="no-data-message">
            Šiai dienai kainų duomenų kol kas neturime. ✨
          </div>
        )}
      </div>
      
      {prices.length > 0 && (
        <div className="price-stats">
          <div className="stat">
            <span className="stat-label">Minimali:</span>
            <span className="stat-value">
              {(Math.min(...prices.map(p => p.price)) * vatMultiplier).toFixed(4)} EUR/kWh
            </span>
          </div>
          <div className="stat">
            <span className="stat-label">Vidutinė:</span>
            <span className="stat-value">
              {((prices.reduce((sum, p) => sum + p.price, 0) / prices.length) * vatMultiplier).toFixed(4)} EUR/kWh
            </span>
          </div>
          <div className="stat">
            <span className="stat-label">Maksimali:</span>
            <span className="stat-value">
              {(Math.max(...prices.map(p => p.price)) * vatMultiplier).toFixed(4)} EUR/kWh
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default PriceChart
